
import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Introduction to Financial Planning for Filipinos',
    content: [
      'Financial planning is essential for every Filipino, helping you achieve security, reduce stress, and build a better future. Explore these resources and tips tailored for the Philippine context:',
      `<ul>
        <li><strong>Budgeting:</strong> Learn to track income and expenses using the <a href="https://www.bsp.gov.ph/Pages/ConsumerFinance/Budgeting.aspx" target="_blank" rel="noopener noreferrer">Bangko Sentral ng Pilipinas (BSP) Budgeting Guide</a>. Use simple tools like the 50/30/20 rule to allocate needs, wants, and savings.</li>
        <li><strong>Saving & Investing:</strong> Discover government-backed savings programs such as <a href="https://www.pagibigfund.gov.ph/" target="_blank" rel="noopener noreferrer">Pag-IBIG Fund</a> and <a href="https://www.sss.gov.ph/" target="_blank" rel="noopener noreferrer">SSS PESO Fund</a>. Learn about low-risk investments like government bonds and UITFs from trusted banks.</li>
        <li><strong>Debt Management:</strong> Get tips from <a href="https://www.bsp.gov.ph/Pages/ConsumerFinance/DebtManagement.aspx" target="_blank" rel="noopener noreferrer">BSP's Debt Management resources</a> to avoid loan traps and manage credit card debt. Seek help from financial counselors if needed.</li>
        <li><strong>Emergency Fund:</strong> Build an emergency fund for unexpected events. Start small and grow your fund over time, aiming for at least 3-6 months of living expenses.</li>
        <li><strong>Financial Literacy:</strong> Join free webinars and workshops from <a href="https://www.bsp.gov.ph/Pages/ConsumerFinance/FinancialEducation.aspx" target="_blank" rel="noopener noreferrer">BSP</a> and <a href="https://www.pfa.org.ph/" target="_blank" rel="noopener noreferrer">Philippine Financial Association</a> to improve your financial knowledge.</li>
      </ul>`
    ]
  },
  {
    title: 'Tracking Income and Expenses',
    content: [
      'List all sources of income (salary, remittances, side jobs) and track monthly expenses. Use free tools from <a href="https://www.bsp.gov.ph/Pages/ConsumerFinance/Budgeting.aspx" target="_blank">BSP</a> or simple spreadsheets.'
    ]
  },
  {
    title: 'The 50/30/20 Rule',
    content: [
      'Allocate 50% of income to needs, 30% to wants, and 20% to savings. Adjust as needed for your situation. This rule is recommended by financial experts and the BSP.'
    ]
  },
  {
    title: 'Practical Tips for Filipinos',
    content: [
      'Prioritize essentials, avoid unnecessary debt, and set realistic savings goals. Consider family support and emergencies when planning your budget.'
    ]
  }
];

const FinancialPlanningModule1Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep } = useModuleProgress('financialPlanningM1');
  const totalSteps = moduleContent.length;
  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;
  const isLastStep = safeCurrentStep === totalSteps - 1;

  const handleNext = () => {
    if (safeCurrentStep < totalSteps - 1) {
      setCurrentStep(safeCurrentStep + 1);
    }
  };
  const handleBack = () => {
    if (safeCurrentStep === 0) {
      navigate('/panel/financial-planning');
      return;
    }
    setCurrentStep(safeCurrentStep - 1);
  };

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left financial-panel">
          <div className="module-header">
            <h1>Module 1</h1>
            <p className="module-subtitle">Budgeting</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Learn the basics of budgeting, tracking income and expenses, and setting realistic savings goals for Filipinos.
            </p>
          </div>
        </div>
        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {safeCurrentStep + 1} of {totalSteps}
              </span>
            </div>
            <div className="module-section">
              <h2 className="module-title">{currentModule.title}</h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>
            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph, index) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph, index) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {safeCurrentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>
            {isLastStep ? (
              <button className="btn-primary" onClick={() => navigate('/panel/financial-planning')}>Finish Module</button>
            ) : (
              <button className="btn-primary" onClick={handleNext}>Next</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialPlanningModule1Page;

